// packages/core/src/learning/schools/fi-math.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "FI math". Matches boglehead-indexing.ts gold standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — held [] for the post-authoring cross-ref
//                     pass. Worldview names: mr-money-mustache, jl-collins,
//                     grant-sabatier, vicki-robin.
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const FI_MATH_EXHIBIT: SchoolExhibitEntry = {
	id: 'fi-math',
	tagline: 'FI math believes freedom gets clearer when time has numbers.',
	worldviewSummary:
		'FI math is paying attention to the relationship between spending, savings rate, invested assets, withdrawal assumptions, runway, and time. This lens protects you from treating financial freedom like a vague dream or a personality trait. It accepts a tradeoff: models are useful, but they are not promises. The blind spot is false certainty. A spreadsheet can make the future feel cleaner than real life, even though income, markets, inflation, health, family needs, and spending can change.',
	coreBeliefs: [
		'FI math believes freedom should be translated into numbers before it becomes a slogan.',
		'FI math believes savings rate often matters more than income because it affects both fuel and lifestyle cost.',
		'FI math believes spending is powerful because it changes the size of the freedom target.',
		'FI math believes runway matters because short term stability changes long term choices.',
		'FI math believes assumptions should be visible, not hidden inside a comforting projection.',
		'FI math believes the point of the math is better judgment, not pretending the future is guaranteed.',
	],
	warnsAgainst: [
		'Treating financial independence like a vague dream instead of a testable plan.',
		'Ignoring spending because income feels more exciting.',
		'Confusing a projection with a promise.',
		'Letting the spreadsheet replace real life judgment.',
	],
	helps: 'This lens helps people who want clear numbers around savings rate, runway, freedom timelines, and tradeoffs. It may not fit someone who wants motivation without math or someone who treats models as certainty instead of a thinking tool.',
	figures: [],
	canon: [
		{
			title: 'Savings rate math',
			oneLineIdea:
				'Savings rate matters because it affects how much you invest and how expensive your future life needs to be.',
		},
		{
			title: 'FI number',
			oneLineIdea:
				'A freedom target turns vague independence into a number you can test, question, and adjust.',
		},
		{
			title: 'Withdrawal rate thinking',
			oneLineIdea:
				'Spending from assets depends on assumptions, risk, time horizon, and flexibility.',
		},
		{
			title: 'Runway math',
			oneLineIdea:
				'Cash and lower expenses buy time, which can turn panic into choices.',
		},
		{
			title: 'The 4 percent rule',
			oneLineIdea:
				'A rule of thumb can guide thinking, but it should not be treated like a personal guarantee.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-fi-math',
	debateIds: [],
	applyActions: [
		{
			label: 'See what your savings rate does to your freedom date',
			action: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
		},
		{
			label: 'Build a freedom plan from real assumptions',
			action: {
				type: 'buildFreedomPlan',
				label: 'Build your freedom plan',
			},
		},
		{
			label: 'Check how much runway changes your options',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Write which assumption could break your plan first',
			action: {
				type: 'noToolAction',
				label: 'Name the weak assumption',
				prompt: 'Write the one assumption in your money plan that would hurt most if it changed.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'FI math asks the question most people avoid: what does freedom actually cost?',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of spending is not just money. It is time you have to keep selling.',
			kind: 'hook',
		},
		{
			line: 'What number made financial freedom feel real instead of vague?',
			kind: 'interview-question',
		},
		{
			line: 'Where does FI math help people, and where does it become false certainty?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps users turn freedom into numbers without pretending the numbers are destiny.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'The app teaches users to test savings rate, runway, and timelines before life tests them.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience how changing spending changes the size of the freedom target.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask someone what they would do differently if they knew the time cost of their lifestyle.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
